import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { api } from "./api";

const KEY = "ri:actions";
const DEVICE_KEY = "ri:device";
const Ctx = createContext(null);

const deviceId = () => {
  let d = localStorage.getItem(DEVICE_KEY);
  if (!d) { d = uuid(); localStorage.setItem(DEVICE_KEY, d); }
  return d;
};
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };

const cache = {};
const listeners = new Map();
let pending = new Set();
let timer = null;
const notify = (id) => listeners.get(id)?.forEach((fn) => fn(cache[id]));
const flush = async () => {
  const ids = [...pending];
  pending = new Set();
  timer = null;
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    try {
      const r = await api.get("/participation/counts", { params: { ids: chunk.join(",") } });
      Object.entries(r.data).forEach(([id, c]) => { cache[id] = c; notify(id); });
    } catch { /* counts are non-critical */ }
  }
};
const request = (id) => { pending.add(id); if (!timer) timer = setTimeout(flush, 40); };

export const useCounts = (id) => {
  const [c, setC] = useState(cache[id]);
  useEffect(() => {
    if (!listeners.has(id)) listeners.set(id, new Set());
    listeners.get(id).add(setC);
    if (!cache[id]) request(id);
    return () => listeners.get(id)?.delete(setC);
  }, [id]);
  return c;
};

const snapshot = (o) => ({ id: o.id, title: o.title, organization: o.organization, category: o.category, city: o.city, province: o.province,
  event_date: o.event_date || null, commitment_type: o.commitment_type, signup_url: o.signup_url });

export const ParticipationProvider = ({ children }) => {
  const [mine, setMine] = useState(load);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(mine)); }, [mine]);
  const toggle = useCallback(async (o, kind, context) => {
    const on = !mine[o.id]?.[kind];
    setMine((m) => {
      const cur = { ...(m[o.id] || { o: snapshot(o) }) };
      if (on) cur[kind] = new Date().toISOString(); else delete cur[kind];
      const next = { ...m, [o.id]: cur };
      if (!cur.interested && !cur.attended) delete next[o.id];
      return next;
    });
    try {
      const r = await api.post("/participation", { listing_id: o.id, device_id: deviceId(), kind, on, context });
      cache[o.id] = r.data; notify(o.id);
    } catch { /* local state still reflects the choice */ }
  }, [mine]);
  const recordView = useCallback((id) => { api.post("/participation/view", { listing_id: id, device_id: deviceId() }).catch(() => {}); }, []);
  const value = useMemo(() => ({ mine, toggle, recordView }), [mine, toggle, recordView]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useParticipation = () => useContext(Ctx);
